import { ArrowRight } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Row } from 'src/shared/components/Semantic/all'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from 'src/shared/components/ui/card'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'src/shared/components/ui/form'
import { Input } from 'src/shared/components/ui/input'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { FormData } from '../types'
import { useLocalization } from 'src/shared/hooks/useLocalization'

export const Pricing = ({
  methods,
  isMyAccount,
}: {
  methods: UseFormReturn<FormData>
  isMyAccount: boolean
}) => {
  const { setValue, control } = methods
  const { t } = useLocalization()
  // Watch all pricing fields
  const cost = useWatch({ control, name: 'cost' })
  const margin = useWatch({ control, name: 'margin' })
  const price = useWatch({ control, name: 'price' })
  const profit = useWatch({ control, name: 'profit' })

  // Track previous values to detect which field changed
  const prevValues = useRef({ cost, margin, price, profit })
  const isCalculating = useRef(false)

  // Helper function to parse numeric value
  const parseValue = (
    val: number | string | undefined | null
  ): number | null => {
    if (val === null || val === undefined || val === '') return null
    const num = typeof val === 'string' ? parseFloat(val) : val
    return isNaN(num) ? null : num
  }

  useEffect(() => {
    // Skip if we're in the middle of a calculation to avoid infinite loops
    if (isCalculating.current) {
      prevValues.current = { cost, margin, price, profit }
      return
    }

    const costNum = parseValue(cost)
    const marginNum = parseValue(margin)
    const priceNum = parseValue(price)
    const profitNum = parseValue(profit)

    const prevCost = parseValue(prevValues.current.cost)
    const prevMargin = parseValue(prevValues.current.margin)
    const prevPrice = parseValue(prevValues.current.price)
    const prevProfit = parseValue(prevValues.current.profit)

    // Determine which field changed
    const costChanged = costNum !== prevCost && prevCost !== null
    const marginChanged = marginNum !== prevMargin && prevMargin !== null
    const priceChanged = priceNum !== prevPrice && prevPrice !== null
    const profitChanged = profitNum !== prevProfit && prevProfit !== null

    // Count how many fields changed
    const changedCount = [
      costChanged,
      marginChanged,
      priceChanged,
      profitChanged,
    ].filter(Boolean).length

    // Skip if nothing changed or if this is initial load (all fields changed)
    if (changedCount === 0 || changedCount > 1) {
      prevValues.current = { cost, margin, price, profit }
      return
    }

    isCalculating.current = true

    try {
      // If cost or margin changed, calculate price and profit
      if (costChanged || marginChanged) {
        if (
          costNum !== null &&
          marginNum !== null &&
          marginNum >= 0 &&
          marginNum < 100
        ) {
          // Formula: price = cost / (1 - margin / 100)
          const calculatedPrice = isMyAccount
            ? costNum
            : Math.round(costNum / (1 - marginNum / 100))
          const calculatedProfit = Math.round(calculatedPrice - costNum)

          setValue('price', calculatedPrice, { shouldDirty: true })
          setValue('profit', calculatedProfit, { shouldDirty: true })
        }
      }
      // If price changed, calculate margin and profit
      else if (priceChanged) {
        if (costNum !== null && priceNum !== null && priceNum > 0) {
          // Formula: margin = (1 - cost / price) * 100
          const calculatedMargin =
            Math.round((1 - costNum / priceNum) * 100 * 100) / 100
          const calculatedProfit = Math.round(priceNum - costNum)

          setValue('margin', calculatedMargin, { shouldDirty: true })
          setValue('profit', calculatedProfit, { shouldDirty: true })
        }
      }
      // If profit changed, calculate price and margin
      else if (profitChanged) {
        if (costNum !== null && profitNum !== null) {
          const calculatedPrice = Math.round(costNum + profitNum)
          if (calculatedPrice > 0) {
            // Formula: margin = (1 - cost / price) * 100
            const calculatedMargin =
              Math.round((1 - costNum / calculatedPrice) * 100 * 100) / 100

            setValue('price', calculatedPrice, { shouldDirty: true })
            setValue('margin', calculatedMargin, { shouldDirty: true })
          }
        }
      }
    } finally {
      isCalculating.current = false
      prevValues.current = { cost, margin, price, profit }
    }
  }, [cost, margin, price, profit, setValue])

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Pricing', 'Pris')}</CardTitle>
      </CardHeader>

      <CardContent>
        <Row className="gap-2 items-end">
          <FormField
            control={control}
            name="cost"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-muted-foreground">
                  <span>
                    {t('Cost', 'Kostnad')}{' '}
                    <span className="text-red-500">*</span>
                  </span>
                </FormLabel>

                <FormControl>
                  <Input {...field} placeholder="0" type="number" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!isMyAccount && (
            <>
              <FormField
                control={control}
                name="margin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-muted-foreground">
                      <span>
                        {t('Margin', 'Marginal')} (%){' '}
                        <span className="text-red-500">*</span>
                      </span>
                    </FormLabel>

                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('Margin (%)', 'Marginal (%)')}
                        type="number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ArrowRight className="h-4 w-4 mb-5" />
              <FormField
                control={control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-muted-foreground">
                      <span>
                        {t('Price', 'Pris')}{' '}
                        <span className="text-red-500">*</span>
                      </span>
                    </FormLabel>

                    <FormControl>
                      <Input {...field} placeholder="0" type="number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ArrowRight className="h-4 w-4 mb-5" />
              <FormField
                control={control}
                name="profit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-muted-foreground">
                      <span>
                        {t('Profit', 'Vinst')}{' '}
                        <span className="text-red-500">*</span>
                      </span>
                    </FormLabel>

                    <FormControl>
                      <Input {...field} placeholder="0" type="number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </Row>
      </CardContent>
    </Card>
  )
}
